import { defineStore } from 'pinia'

let nextId = 1

/**
 * オブジェクトが「固定」(削除禁止) かを判定。デフォルトデザインで配置された
 * 編集制限付き要素の保護に使用。
 *  - text: lockPosition / lockResize / lockContent のいずれかが true なら固定
 *  - frame / 画像 (stamp/picture): movable=false または scalable=false なら固定
 * 注: frame は表示上 Transformer 非対応 (DecoCanvas 側で別途制御) だが、
 *     ユーザー追加フレームは差し替え・削除可能。default design で配置された
 *     場合のみ movable/scalable=false がセットされ削除不可になる。
 */
function isObjectFixed(obj) {
    if (!obj) return false
    if (obj.objectType === 'text') {
        return obj.lockPosition === true
            || obj.lockResize   === true
            || obj.lockContent  === true
    }
    return obj.movable === false || obj.scalable === false
}

export const useCanvasStore = defineStore('canvas', {
    state: () => ({
        // object shape: {
        //   id, objectType, x, y, width, height, rotation,
        //   src?, content?, font?, weight?, color?, fontSize?, decoId?,
        //   // 編集制限フラグ (default design 由来):
        //   movable?,       // frame/stamp: ドラッグ可否 (default true)
        //   scalable?,      // frame/stamp: リサイズ/回転可否 (default: frame=false, stamp=true)
        //   lockPosition?,  // text: ドラッグ禁止
        //   lockResize?,    // text: Transformer禁止
        //   lockContent?,   // text: content/font/color/direction 編集禁止 & 削除禁止
        //   lockFont?,      // text: font/color/direction のみ禁止 (content は可)
        //   autoShrink?,    // text: { enabled, box: {width,height}, fontSizeMin }
        //   slotKey?,       // text: グループ間 identity 用
        // }
        objects: [],
        selectedId: null,
        aspectRatio: 1.0,
        isCircularLabel: false,
        canvasSize: 1200,    // 内部解像度の基準 (ラベルの長辺 = 1200)
        displayScale: typeof window !== 'undefined'
            ? Math.min(1, Math.min(
                (window.innerWidth  - 40) / 1200,
                (window.innerHeight - 152) / 1200
              ))
            : 0.3,
        // labelRegion は常に {x:0, y:0, width:lw, height:lh} (ステージ = ラベル領域)
        labelRegion: { x: 0, y: 0, width: 1200, height: 1200 },
        cntPicture: 0,
        cntStamp: 0,
    }),
    getters: {
        selectedObject: (state) => state.objects.find(o => o.id === state.selectedId) ?? null,
        // 選択中オブジェクトが「固定」(削除不可) かどうか
        selectedIsFixed() {
            return isObjectFixed(this.selectedObject)
        },
        maxPicture: () => 100,
        maxStamp: () => 100,
        // ラベル中心座標 (オブジェクト配置の基準点)
        labelCenterX: (state) => state.labelRegion.width  / 2,
        labelCenterY: (state) => state.labelRegion.height / 2,
    },
    actions: {
        initStage(aspectRatio, isCircular) {
            this.aspectRatio  = aspectRatio
            this.isCircularLabel = isCircular

            const size = this.canvasSize   // 1200 = 長辺の基準
            let lw = size, lh = size
            if (aspectRatio > 1) {
                // 横長ラベル: 幅 = 1200, 高さ = 1200/ratio
                lh = Math.round(size / aspectRatio)
            } else if (aspectRatio > 0 && aspectRatio < 1) {
                // 縦長ラベル: 幅 = 1200*ratio, 高さ = 1200
                lw = Math.round(size * aspectRatio)
            }
            // ステージ = ラベル領域 (原点 0,0 から)
            this.labelRegion = { x: 0, y: 0, width: lw, height: lh }

            // CSS表示スケール: ラベル全体が画面内に収まるように両辺で制約
            this.displayScale = Math.min(1,
                (window.innerWidth  - 40)  / lw,
                (window.innerHeight - 152) / lh
            )
        },

        addObject(obj) {
            this.objects.push({ id: nextId++, ...obj })
            if (obj.objectType === 'picture') this.cntPicture++
            if (obj.objectType === 'stamp')   this.cntStamp++
        },

        removeObject(id) {
            const idx = this.objects.findIndex(o => o.id === id)
            if (idx === -1) return
            const obj = this.objects[idx]
            // 固定オブジェクト (デフォルトデザインで配置 + 編集制限あり) は削除拒否
            if (isObjectFixed(obj)) return
            if (obj.objectType === 'picture') this.cntPicture--
            if (obj.objectType === 'stamp')   this.cntStamp--
            this.objects.splice(idx, 1)
            if (this.selectedId === id) this.selectedId = null
        },

        /**
         * デフォルトデザイン由来 (fromDesign=true) のオブジェクトを一括削除する。
         * デザイングループ切り替え時に、前グループの配置物だけを差し替える用途。
         * 編集制限 (isObjectFixed) の有無に関わらず削除する点が removeObject と異なる。
         * ユーザーが自分で追加した写真/スタンプ/テキストは残す。
         */
        clearDesignObjects() {
            const remaining = []
            for (const obj of this.objects) {
                if (obj.fromDesign === true) {
                    if (obj.objectType === 'picture') this.cntPicture--
                    if (obj.objectType === 'stamp')   this.cntStamp--
                    if (this.selectedId === obj.id) this.selectedId = null
                    continue
                }
                remaining.push(obj)
            }
            this.objects = remaining
        },

        updateObject(id, patch) {
            const obj = this.objects.find(o => o.id === id)
            if (!obj) return
            // 防御層: lockPosition なら x/y の更新を無視、lockResize なら fontSize/width/height/rotation を無視
            const safe = { ...patch }
            if (obj.lockPosition === true) {
                delete safe.x
                delete safe.y
            }
            if (obj.lockResize === true) {
                delete safe.width
                delete safe.height
                delete safe.fontSize
                delete safe.rotation
            }
            if (obj.scalable === false) {
                delete safe.width
                delete safe.height
                delete safe.rotation
            }
            if (obj.lockContent === true) {
                // content / font 系の編集を全面禁止
                delete safe.content
                delete safe.font
                delete safe.weight
                delete safe.color
                delete safe.direction
                delete safe.fontSize
            } else if (obj.lockFont === true) {
                // content は可、font/weight/color/direction のみ禁止
                delete safe.font
                delete safe.weight
                delete safe.color
                delete safe.direction
            }
            Object.assign(obj, safe)
        },

        selectObject(id)  { this.selectedId = id },
        deselectAll()     { this.selectedId = null },

        moveUp(id) {
            const idx = this.objects.findIndex(o => o.id === id)
            if (idx < this.objects.length - 1) {
                const [obj] = this.objects.splice(idx, 1)
                this.objects.splice(idx + 1, 0, obj)
            }
        },
        moveDown(id) {
            const idx = this.objects.findIndex(o => o.id === id)
            if (idx > 0) {
                const [obj] = this.objects.splice(idx, 1)
                this.objects.splice(idx - 1, 0, obj)
            }
        },

        centerH(id) {
            this.updateObject(id, { x: this.labelRegion.width  / 2 })
        },
        centerV(id) {
            this.updateObject(id, { y: this.labelRegion.height / 2 })
        },

        updateScale() {
            const r = this.labelRegion
            this.displayScale = Math.min(1,
                (window.innerWidth  - 40)  / r.width,
                (window.innerHeight - 152) / r.height
            )
        },
    },
})
